import React, { useState, useEffect, useRef } from 'react';
import { Search, MapPin, Clock, Truck, Home as HomeIcon, Tag, X, Calendar, Users, ArrowRight, GraduationCap, PartyPopper, Wallet, Handshake, ChevronDown, Newspaper, Briefcase, DollarSign, Heart, Landmark, Music } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import BusinessCard from '../components/BusinessCard';
import HorizontalCarousel from '../components/HorizontalCarousel';
import CategoryCard from '../components/CategoryCard';
import BenefitsSlider from '../components/BenefitsSlider';
import PartnersSlider from '../components/PartnersSlider';
import VitrinesBanners from '../components/home/VitrinesBanners';
import BannerCarousel from '../components/BannerCarousel';
import TagsEmAltaWidget from '../components/TagsEmAltaWidget';
import { getUserChosenMunicipio } from '../services/userChosenMunicipio';
import { useLocation } from '../context/LocationContext';
import { PublicAlertsHome } from '../components/PublicAlerts';
import { AlertSubscriptionButton } from '../components/AlertSubscription';
// Use centralized API service
import { 
  businessesApi, 
  categoriesApi, 
  bairrosApi, 
  programsApi,
  promotionsApi,
  noticiasApi,
  jobsApi,
  socialApi 
} from '../services/api';
import SEOHead from '../components/SEOHead';
import ColetivosWidget from '../components/ColetivosWidget';
import ProximasAcoesWidget from '../components/ProximasAcoesWidget';
import FeedWidget from '../components/FeedWidget';
import CidadeBuscandoWidget from '../components/CidadeBuscandoWidget';
import AuthModal from '../components/AuthModal';
import HeroCarousel from '../components/HeroCarousel';
import { useAuth } from '../context/AuthContext';
import useSiteConfig from '../hooks/useSiteConfig';
import { resolveBackendImage } from '../utils/brandLogo';

const HERO_BG = 'https://customer-assets.emergentagent.com/job_09488136-b9ae-4999-9fcb-3a7926997fb3/artifacts/i1q8wd5j_hero.png';

const programTypeConfig = {
  capacitacao: { label: 'Capacitação', icon: GraduationCap, color: 'bg-blue-100 text-blue-700' },
  evento: { label: 'Evento', icon: PartyPopper, color: 'bg-purple-100 text-purple-700' },
  acao: { label: 'Ação', icon: Users, color: 'bg-teal-100 text-teal-700' },
  institucional: { label: 'Institucional', icon: Landmark, color: 'bg-indigo-100 text-indigo-700' },
  cultural: { label: 'Cultural', icon: Music, color: 'bg-pink-100 text-pink-700' },
  financeiro: { label: 'Financeiro', icon: Wallet, color: 'bg-green-100 text-green-700' },
  parceria: { label: 'Parceria', icon: Handshake, color: 'bg-orange-100 text-orange-700' },
};

export default function Home() {
  const navigate = useNavigate();
  const { municipioSlug } = useParams(); // Multi-tenant: get municipality from URL
  const base = municipioSlug ? `/${municipioSlug}` : '';
  const { location } = useLocation();
  const { user } = useAuth();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const searchRef = useRef(null);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [nearbyBusinesses, setNearbyBusinesses] = useState([]);
  const [nearbyRadius, setNearbyRadius] = useState(5); // km — Item 3 do edital
  const [newBusinesses, setNewBusinesses] = useState([]);
  const [allBusinesses, setAllBusinesses] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [selectedCity, setSelectedCity] = useState(() => getUserChosenMunicipio());
  const { config: siteConfig } = useSiteConfig();
  const [upcomingPrograms, setUpcomingPrograms] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [bairros, setBairros] = useState([]);
  const [selectedBairro, setSelectedBairro] = useState('');
  const [showBairroDropdown, setShowBairroDropdown] = useState(false);
  const [latestNews, setLatestNews] = useState([]);
  const [jobsCount, setJobsCount] = useState(0);
  const [featuredOrgs, setFeaturedOrgs] = useState([]);
  const [upcomingActivities, setUpcomingActivities] = useState([]);
  const bairroRef = useRef(null);
  
  // Autocomplete states
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompleteResults, setAutocompleteResults] = useState({
    businesses: [],
    categories: []
  });

  const [activeFilters, setActiveFilters] = useState({
    nearby: false,
    open: false,
    delivery: false,
    home_service: false,
    promo: false,
    hiring: false,
    faixa_preco: '' // '', '$', '$$', '$$$'
  });

  useEffect(() => {
    loadData();
    loadJobsCount();
    loadSocialData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, selectedCity, selectedBairro, municipioSlug, nearbyRadius]);

  // Close autocomplete when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowAutocomplete(false);
      }
      if (bairroRef.current && !bairroRef.current.contains(event.target)) {
        setShowBairroDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load categories using centralized API
      const categoriesData = await categoriesApi.list();
      setAllCategories(categoriesData);
      setCategories(categoriesData.slice(0, 8));

      // Load bairros for neighborhood filter (filtered by municipality)
      try {
        const bairrosData = await bairrosApi.list(municipioSlug);
        setBairros(bairrosData);
      } catch (e) {
        console.error('Erro ao carregar bairros:', e);
      }

      // Build params for v2 API (no need for manual municipio_id - handled by API service)
      const params = location ? {
        lat: location.latitude,
        lon: location.longitude,
        limit: 50
      } : { limit: 50 };

      // Add city filter if selected
      if (selectedCity) {
        params.cidade = selectedCity.nome;
      }

      // Add neighborhood filter if selected
      if (selectedBairro) {
        params.bairro = selectedBairro;
      }

      // Multi-tenant: add municipality filter for v1 endpoints
      if (municipioSlug) {
        params.municipio_slug = municipioSlug;
      }

      // Load all businesses using v2 API (tenant filtering is automatic)
      const allData = await businessesApi.list(params);
      setAllBusinesses(allData);
      setFilteredBusinesses(allData);

      // Load nearby businesses
      if (location) {
        try {
          const nearbyParams = {
            lat: location.latitude,
            lon: location.longitude,
            radius_km: nearbyRadius,
            limit: 10
          };
          if (municipioSlug) nearbyParams.municipio_slug = municipioSlug;
          const nearbyData = await businessesApi.nearby(nearbyParams);
          setNearbyBusinesses(nearbyData);
        } catch (e) {
          console.error('Erro ao carregar negócios próximos:', e);
        }
      }

      // Load new businesses
      try {
        const newParams = { limit: 10 };
        if (selectedCity) newParams.cidade = selectedCity.nome;
        if (municipioSlug) newParams.municipio_slug = municipioSlug;
        const newData = await businessesApi.new(newParams);
        setNewBusinesses(newData);
      } catch (e) {
        console.error('Erro ao carregar novos negócios:', e);
      }

      // Load UPCOMING programs (data_inicio >= hoje) — endpoint /upcoming
      // já filtra corretamente. Não usar `programsApi.list` aqui: aquele
      // endpoint devolve TODOS os programas, inclusive já vencidos.
      try {
        const programsData = await programsApi.upcoming(municipioSlug);
        setUpcomingPrograms((programsData || []).slice(0, 4));
      } catch (e) {
        console.error('Erro ao carregar programas:', e);
      }

      // Load promotions using centralized API (tenant filtering is automatic in v2)
      try {
        const promosData = await promotionsApi.list();
        setPromotions(promosData.slice(0, 4));
      } catch (e) {
        console.error('Erro ao carregar promoções:', e);
      }

      // Load latest news using centralized API
      try {
        const newsData = await noticiasApi.list({ limit: 4, municipio_slug: municipioSlug });
        setLatestNews(newsData.noticias || []);
      } catch (e) {
        console.error('Erro ao carregar notícias:', e);
      }

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadJobsCount = async () => {
    try {
      // V2 jobs/count handles tenant filtering automatically
      const data = await jobsApi.count('active');
      const count = data.count || 0;
      setJobsCount(count);
    } catch (error) {
      console.error('Erro ao carregar contagem de vagas:', error);
    }
  };

  const loadSocialData = async () => {
    try {
      // Load featured social organizations using centralized API
      const orgsData = await socialApi.featured(municipioSlug, 4);
      setFeaturedOrgs(orgsData || []);

      // Load upcoming activities from social organizations
      const activitiesData = await socialApi.upcomingActivities(municipioSlug, 4);
      setUpcomingActivities(activitiesData || []);
    } catch (error) {
      console.error('Erro ao carregar dados sociais:', error);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      setFilteredBusinesses(allBusinesses);
      setShowAutocomplete(false);
      setAutocompleteResults({ businesses: [], categories: [] });
      return;
    }

    const queryLower = query.toLowerCase();

    // Filter businesses for autocomplete
    const matchedBusinesses = allBusinesses.filter(b => 
      b.nome?.toLowerCase().includes(queryLower) ||
      b.descricao?.toLowerCase().includes(queryLower) ||
      b.categoria?.toLowerCase().includes(queryLower) ||
      b.bairro?.toLowerCase().includes(queryLower)
    ).slice(0, 5);

    // Filter categories for autocomplete
    const matchedCategories = allCategories.filter(c =>
      c.nome?.toLowerCase().includes(queryLower)
    ).slice(0, 3);

    setAutocompleteResults({
      businesses: matchedBusinesses,
      categories: matchedCategories
    });
    
    setShowAutocomplete(matchedBusinesses.length > 0 || matchedCategories.length > 0);

    // Update filtered businesses list
    const filtered = allBusinesses.filter(b => 
      b.nome?.toLowerCase().includes(queryLower) ||
      b.descricao?.toLowerCase().includes(queryLower) ||
      b.categoria?.toLowerCase().includes(queryLower) ||
      b.bairro?.toLowerCase().includes(queryLower)
    );
    setFilteredBusinesses(filtered);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery.trim()) params.set('busca', searchQuery.trim());
    if (selectedBairro) params.set('bairro', selectedBairro);
    if (activeFilters.open) params.set('aberto_agora', 'true');
    if (activeFilters.delivery) params.set('entrega', 'true');
    if (activeFilters.home_service) params.set('atende_domicilio', 'true');
    if (activeFilters.promo) params.set('promocao', 'true');
    if (activeFilters.faixa_preco) params.set('faixa_preco', activeFilters.faixa_preco);

    // Only navigate if there's at least a search term, bairro or active filter
    const hasAny = Array.from(params.keys()).length > 0 || activeFilters.nearby;
    if (!hasAny) return;

    const basePath = municipioSlug ? `/${municipioSlug}` : '';
    const qs = params.toString();
    navigate(`${basePath}/explorar${qs ? `?${qs}` : ''}`);
    setShowAutocomplete(false);
  };

  const handleSelectBusiness = (business) => {
    setShowAutocomplete(false);
    navigate(`${base}/negocio/${business.id}`);
  };

  const handleSelectCategory = (category) => {
    setShowAutocomplete(false);
    setSearchQuery('');
    // SEO-friendly canonical URL: /:municipio/:categoria-slug
    const slug = category.slug || category.id;
    navigate(municipioSlug ? `/${municipioSlug}/${slug}` : `/explorar?categoria=${category.id}`);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setFilteredBusinesses(allBusinesses);
    setShowAutocomplete(false);
  };

  const toggleFilter = (filterKey) => {
    setActiveFilters(prev => ({
      ...prev,
      [filterKey]: !prev[filterKey]
    }));
  };

  const handleHiringClick = () => {
    // Navigate to jobs page instead of filtering
    const basePath = municipioSlug ? `/${municipioSlug}` : '';
    navigate(`${basePath}/vagas`);
  };

  const chips = [
    { key: 'nearby', label: 'Perto de mim', icon: MapPin },
    { key: 'promo', label: 'Promoções', icon: Tag },
    { key: 'open', label: 'Aberto agora', icon: Clock },
    { key: 'delivery', label: 'Entrega', icon: Truck },
    { key: 'home_service', label: 'Atende em domicílio', icon: HomeIcon },
  ];

  const priceRanges = [
    { value: '', label: 'Todos os preços' },
    { value: '$', label: '$ Econômico' },
    { value: '$$', label: '$$ Moderado' },
    { value: '$$$', label: '$$$ Premium' },
  ];

  // Apply filters to businesses
  const applyFilters = (businesses) => {
    return businesses.filter(b => {
      // Price range filter
      if (activeFilters.faixa_preco && b.faixa_preco !== activeFilters.faixa_preco) return false;
      
      // Delivery filter
      if (activeFilters.delivery && !b.faz_entrega) return false;
      
      // Home service filter
      if (activeFilters.home_service && !b.atende_domicilio) return false;
      
      // Promotion filter
      if (activeFilters.promo && !b.tem_promocao) return false;
      
      return true;
    });
  };

  const filteredNearbyBusinesses = applyFilters(nearbyBusinesses);
  const filteredNewBusinesses = applyFilters(newBusinesses);
  const filteredPopularBusinesses = applyFilters(allBusinesses).slice(0, 10);
  const displayCategories = showAllCategories ? allCategories : categories;

  return (
    <div className="min-h-screen bg-[var(--background)] pb-20">
      <SEOHead
        title={selectedCity ? `Negócios em ${selectedCity.nome}, ${selectedCity.uf || 'MG'}` : 'Negócios Locais'}
        description={selectedCity
          ? `Encontre os melhores negócios, serviços e profissionais em ${selectedCity.nome}, ${selectedCity.uf || 'MG'}. Veja avaliações, contatos e promoções.`
          : 'Encontre os melhores negócios locais da sua região. Conecte-se com empreendedores, descubra serviços, produtos e promoções perto de você.'
        }
        canonical={municipioSlug ? `/${municipioSlug}` : '/'}
        preloadImage={siteConfig?.home_hero_bg_url || HERO_BG}
      />
      {/* Hero Section — carrossel dinâmico com fallback ao hero configurável */}
      <HeroCarousel
        municipioId={selectedCity?.id}
        municipioSlug={municipioSlug}
        onCtaClick={(banner, cta) => {
          // Se link vazio, apenas ignora
          if (!cta.link) return true;
          // Comportamento especial: /cadastrar abre AuthModal quando não logado
          if (cta.link === '/cadastrar') {
            if (user) {
              navigate(`${base}/cadastrar`);
            } else {
              setAuthModalOpen(true);
            }
            return true;
          }
          // Rotas internas: usa react-router; externas: default behavior
          if (cta.link.startsWith('/')) {
            navigate(cta.link);
            return true;
          }
          return false;
        }}
        fallback={
          <div
            className="relative bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${siteConfig?.home_hero_bg_url || HERO_BG})` }}
            data-testid="home-hero"
          >
            {/* Overlay for better text readability */}
            <div className="absolute inset-0 bg-gradient-to-r from-[#56007F]/90 via-[#56007F]/70 to-transparent"></div>

            <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-24">
              <div className="max-w-xl">
                <h1
                  className="text-3xl md:text-5xl font-extrabold mb-4 text-white text-left whitespace-pre-line"
                  style={{ fontFamily: 'Manrope' }}
                  data-testid="home-hero-titulo"
                >
                  {siteConfig?.home_hero_titulo || 'O Marketplace oficial\nda cidade!'}
                </h1>
                <p
                  className="text-white/90 text-lg md:text-xl mb-8 text-left whitespace-pre-line"
                  data-testid="home-hero-subtitulo"
                >
                  {siteConfig?.home_hero_subtitulo || 'Fortaleça a economia\nda nossa comunidade'}
                </p>

                {/* CTA Button */}
                <div className="flex flex-wrap gap-4 items-center">
                  <button
                    onClick={() => {
                      const cfgLink = siteConfig?.home_hero_cta_link;
                      const goesToCadastrar = !cfgLink || cfgLink === '/cadastrar';
                      if (goesToCadastrar) {
                        if (user) {
                          navigate(`${base}/cadastrar`);
                        } else {
                          setAuthModalOpen(true);
                        }
                      } else {
                        navigate(cfgLink);
                      }
                    }}
                    className="bg-[#FFA52A] hover:bg-[#e89420] text-[#56007F] font-bold rounded-full px-8 py-4 text-lg transition-all shadow-lg hover:shadow-xl active:scale-95"
                    data-testid="hero-cta-button"
                  >
                    {siteConfig?.home_hero_cta_label || 'Anuncie grátis seu negócio'}
                  </button>

                  <AlertSubscriptionButton
                    municipioId={selectedCity?.id}
                    municipioNome={selectedCity?.nome}
                  />
                </div>
              </div>
            </div>
          </div>
        }
      />

      {/* Search Section */}
      <div className="bg-white border-b border-[var(--border)]">
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Search Bar with Autocomplete */}
          <div className="relative mb-4" ref={searchRef}>
            <form onSubmit={handleSearchSubmit} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-secondary)]" />
                <input
                  type="text"
                  placeholder="Buscar por nome, serviço ou produto..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={() => searchQuery && setShowAutocomplete(true)}
                  className="w-full pl-12 pr-12 py-3.5 rounded-xl border border-[var(--border)] bg-white focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] transition-all shadow-sm placeholder:text-gray-400 text-base"
                  data-testid="search-input"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full"
                  >
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                )}
              </div>
              <button
                type="submit"
                className="px-5 py-3.5 bg-[var(--primary)] text-white rounded-xl font-medium hover:bg-[var(--primary)]/90 transition-all shadow-sm flex items-center gap-2"
                data-testid="search-submit-btn"
              >
                <Search className="w-5 h-5" />
                <span className="hidden sm:inline">Buscar</span>
              </button>
            </form>

            {/* Autocomplete Dropdown */}
            {showAutocomplete && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-[var(--border)] shadow-lg z-50 overflow-hidden">
                {autocompleteResults.categories.length > 0 && (
                  <div className="p-2 border-b border-[var(--border)]">
                    <p className="text-xs font-medium text-gray-500 px-3 py-1">CATEGORIAS</p>
                    {autocompleteResults.categories.map(category => (
                      <button
                        key={category.id}
                        onClick={() => handleSelectCategory(category)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg flex items-center gap-3"
                      >
                        <div className="w-8 h-8 bg-[var(--primary)]/10 rounded-lg flex items-center justify-center">
                          <Tag className="w-4 h-4 text-[var(--primary)]" />
                        </div>
                        <span className="font-medium">{category.nome}</span>
                      </button>
                    ))}
                  </div>
                )}
                
                {autocompleteResults.businesses.length > 0 && (
                  <div className="p-2">
                    <p className="text-xs font-medium text-gray-500 px-3 py-1">NEGÓCIOS</p>
                    {autocompleteResults.businesses.map(business => (
                      <button
                        key={business.id}
                        onClick={() => handleSelectBusiness(business)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg flex items-center gap-3"
                      >
                        <div className="w-10 h-10 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                          {business.foto ? (
                            <img src={resolveBackendImage(business.foto)} alt={business.nome} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <MapPin className="w-5 h-5" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{business.nome}</p>
                          <p className="text-sm text-gray-500 truncate">{business.categoria} • {business.bairro}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {searchQuery && (
                  <button
                    onClick={handleSearchSubmit}
                    className="w-full px-4 py-3 bg-gray-50 text-[var(--primary)] font-medium text-sm hover:bg-gray-100 border-t"
                  >
                    Ver todos os resultados para "{searchQuery}"
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Primary Filters Row: Bairro + Contratando + Faixa de Preço */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {/* Neighborhood Filter Dropdown */}
            <div className="relative" ref={bairroRef}>
              <button
                onClick={() => setShowBairroDropdown(!showBairroDropdown)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border transition-all ${
                  selectedBairro 
                    ? 'border-[var(--primary)] bg-[var(--primary)]/5 text-[var(--primary)]' 
                    : 'border-[var(--border)] bg-white text-[var(--text-secondary)] hover:border-[var(--primary)]'
                }`}
                data-testid="neighborhood-filter-btn"
              >
                <MapPin className="w-4 h-4" />
                <span className="font-medium text-sm">
                  {selectedBairro || 'Filtrar por bairro'}
                </span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showBairroDropdown ? 'rotate-180' : ''}`} />
              </button>

              {selectedBairro && (
                <button
                  onClick={() => setSelectedBairro('')}
                  className="ml-2 p-1.5 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-600 inline-flex"
                  data-testid="clear-neighborhood-btn"
                >
                  <X className="w-4 h-4" />
                </button>
              )}

              {showBairroDropdown && bairros.length > 0 && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl border border-[var(--border)] shadow-lg z-50 max-h-64 overflow-y-auto">
                  <div className="p-2">
                    <p className="text-xs font-medium text-gray-500 px-3 py-1 uppercase">Bairros disponíveis</p>
                    {bairros.map(bairro => (
                      <button
                        key={bairro}
                        onClick={() => {
                          setSelectedBairro(bairro);
                          setShowBairroDropdown(false);
                        }}
                        className={`w-full text-left px-3 py-2 hover:bg-gray-50 rounded-lg flex items-center gap-2 text-sm ${
                          selectedBairro === bairro ? 'bg-[var(--primary)]/10 text-[var(--primary)] font-medium' : ''
                        }`}
                        data-testid={`bairro-option-${bairro}`}
                      >
                        <MapPin className="w-4 h-4" />
                        {bairro}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Hiring/Jobs Button - Special with count */}
            <button
              onClick={handleHiringClick}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all bg-teal-500 text-white hover:bg-teal-600 shadow-sm"
              data-testid="filter-chip-hiring"
            >
              <Briefcase className="w-4 h-4" />
              Contratando
              {jobsCount > 0 && (
                <span className="bg-white/20 px-1.5 py-0.5 rounded-full text-xs font-bold">
                  {jobsCount}
                </span>
              )}
            </button>

            {/* Price Range Dropdown */}
            <div className="relative">
              <button
                onClick={() => setActiveFilters(prev => ({ ...prev, showPriceDropdown: !prev.showPriceDropdown }))}
                className={`flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all border ${
                  activeFilters.faixa_preco
                    ? 'bg-[var(--primary)] text-white border-[var(--primary)]'
                    : 'bg-white border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--primary)]'
                }`}
                data-testid="filter-price-range"
              >
                <DollarSign className="w-4 h-4" />
                {activeFilters.faixa_preco || 'Faixa de Preço'}
                <ChevronDown className={`w-3 h-3 transition-transform ${activeFilters.showPriceDropdown ? 'rotate-180' : ''}`} />
              </button>

              {activeFilters.showPriceDropdown && (
                <div className="absolute top-full mt-2 left-0 bg-white border border-[var(--border)] rounded-xl shadow-lg z-50 min-w-[180px] overflow-hidden">
                  {priceRanges.map(({ value, label }) => (
                    <button
                      key={value}
                      onClick={() => setActiveFilters(prev => ({ ...prev, faixa_preco: value, showPriceDropdown: false }))}
                      className={`w-full text-left px-3 py-2.5 hover:bg-gray-50 text-sm ${
                        activeFilters.faixa_preco === value ? 'bg-[var(--primary)]/10 text-[var(--primary)] font-medium' : ''
                      }`}
                      data-testid={`price-option-${value || 'all'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Explorar por tags — direciona para /tags */}
            <button
              type="button"
              onClick={() => navigate(`${base}/tags`)}
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all border border-[var(--border)] bg-white text-[var(--text-secondary)] hover:border-[var(--primary)] hover:text-[var(--primary)]"
              data-testid="filter-explorar-tags"
              aria-label="Explorar por tags"
            >
              <Tag className="w-4 h-4" />
              Explorar por tags
            </button>
          </div>

          {/* Filter Chips */}
          <div className="flex overflow-x-auto gap-2 pb-2 -mx-4 px-4 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {chips.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => toggleFilter(key)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  activeFilters[key]
                    ? 'bg-[var(--primary)] text-white'
                    : 'bg-white border border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--primary)]'
                }`}
                data-testid={`filter-chip-${key}`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8 space-y-10">
        {/* Public Alerts Section */}
        <PublicAlertsHome municipioId={selectedCity?.id} />

        {/* Categories Section */}
        <section data-testid="categories-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
              Categorias
            </h2>
            <button
              onClick={() => setShowAllCategories(!showAllCategories)}
              className="text-sm font-medium text-[var(--primary)] hover:underline"
              data-testid="see-all-categories"
            >
              {showAllCategories ? 'Ver menos' : 'Ver todas'}
            </button>
          </div>
          <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 ${showAllCategories ? '' : ''}`}>
            {displayCategories.map(category => (
              <CategoryCard
                key={category.id}
                category={category}
                onClick={() => {
                  const slug = category.slug || category.id;
                  navigate(municipioSlug ? `/${municipioSlug}/${slug}` : `/explorar?categoria=${category.id}`);
                }}
              />
            ))}
          </div>
          
          {/* CTA para navegar por categorias */}
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate(`${base}/categorias`)}
              className="inline-flex items-center gap-2 bg-[var(--primary)] text-white px-6 py-3 rounded-full font-semibold hover:bg-[var(--primary)]/90 transition-all shadow-md hover:shadow-lg"
              data-testid="explore-categories-cta"
            >
              Explorar todas as categorias e subcategorias
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </section>

        {/* Banner Carousel - abaixo das categorias */}
        <BannerCarousel municipioSlug={municipioSlug} />

        {/* Vitrines Temáticas: Produtos da Roça + Economia Criativa */}
        <VitrinesBanners municipioSlug={municipioSlug} municipioId={selectedCity?.id} />

        {/* Tags em alta na cidade */}
        {municipioSlug && (
          <div className="mt-6">
            <TagsEmAltaWidget
              municipioSlug={municipioSlug}
              municipioId={selectedCity?.id}
              municipioNome={selectedCity?.nome}
              limit={8}
              minItems={3}
            />
          </div>
        )}

        {/* Upcoming Programs/Events */}
        {upcomingPrograms.length > 0 && (
          <section data-testid="programs-section" className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
                Próximos Eventos
              </h2>
              <button
                onClick={() => navigate(`${base}/programas`)}
                className="text-[var(--primary)] font-medium flex items-center gap-1 hover:underline"
              >
                Ver todos <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {upcomingPrograms.map(program => {
                const config = programTypeConfig[program.tipo] || programTypeConfig.evento;
                const Icon = config.icon;

                return (
                  <div
                    key={program.id}
                    onClick={() => navigate(`${base}/programas`)}
                    className="bg-white rounded-xl border border-[var(--border)] p-4 hover:shadow-md transition-all cursor-pointer group flex flex-col"
                    data-testid={`program-card-${program.id}`}
                  >
                    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color} mb-3 self-start`}>
                      <Icon className="w-3.5 h-3.5" />
                      {config.label}
                    </div>

                    {/* Data em destaque (iter259) */}
                    {(() => {
                      const inicio = program.data_inicio ? new Date(program.data_inicio + 'T00:00:00') : null;
                      const fim = program.data_fim && program.data_fim !== program.data_inicio
                        ? new Date(program.data_fim + 'T00:00:00')
                        : null;
                      if (!inicio) return null;
                      const dia = String(inicio.getDate()).padStart(2, '0');
                      const mes = inicio.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '').toUpperCase();
                      return (
                        <div className="flex items-start gap-3 mb-3">
                          <div className="flex-shrink-0 bg-gradient-to-br from-[var(--primary)] to-purple-700 text-white rounded-lg px-3 py-2 text-center min-w-[56px]">
                            <div className="text-[10px] font-medium leading-none uppercase tracking-wide">{mes}</div>
                            <div className="text-xl font-bold leading-tight mt-0.5">{dia}</div>
                          </div>
                          {fim && (
                            <div className="text-xs text-[var(--text-secondary)] pt-1">
                              até<br />
                              <span className="font-semibold">
                                {String(fim.getDate()).padStart(2, '0')}/{String(fim.getMonth() + 1).padStart(2, '0')}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    <h3 className="font-semibold text-sm mb-2 line-clamp-2 group-hover:text-[var(--primary)] transition-colors">
                      {program.titulo}
                    </h3>

                    {program.descricao_curta && (
                      <p className="text-xs text-[var(--text-secondary)] line-clamp-2 mb-2" data-testid={`program-desc-${program.id}`}>
                        {program.descricao_curta}
                      </p>
                    )}

                    {program.local && (
                      <div className="mt-auto pt-2 text-xs text-[var(--text-muted)] flex items-center gap-1.5 truncate">
                        <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                        <span className="truncate">{program.local}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Promotions/Coupons Section */}
        {promotions.length > 0 && (
          <section data-testid="promotions-section" className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
                🏷️ Ofertas e Cupons
              </h2>
              <button
                onClick={() => navigate(`${base}/campanhas`)}
                className="text-[var(--primary)] font-medium flex items-center gap-1 hover:underline"
              >
                Ver todas <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {promotions.map(promo => (
                <div
                  key={promo.id}
                  onClick={() => navigate(`${base}/negocio/${promo.business_id}`)}
                  className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl border border-orange-200 p-4 hover:shadow-md transition-all cursor-pointer group"
                  data-testid={`promo-card-${promo.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-xs text-[var(--primary)] font-medium">
                      {promo.negocio_nome}
                    </span>
                    <span className="bg-[#FFA52A] text-white text-xs font-bold px-2 py-1 rounded-full">
                      {promo.tipo === 'percentual' ? `${promo.desconto}% OFF` : `R$${promo.preco_promocional}`}
                    </span>
                  </div>
                  <h3 className="font-semibold text-sm mb-2 line-clamp-2 group-hover:text-[var(--primary)] transition-colors">
                    {promo.titulo}
                  </h3>
                  <p className="text-xs text-[var(--text-secondary)] mb-3 line-clamp-2">
                    {promo.descricao}
                  </p>
                  <div className="flex items-center justify-between">
                    <code className="bg-white px-2 py-1 rounded text-xs font-mono font-bold text-[var(--primary)] border border-[var(--primary)]/20">
                      {promo.codigo}
                    </code>
                    <span className="text-xs text-[var(--text-muted)]">
                      até {new Date(promo.validade).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Nearby Businesses */}
        {location && (
          <section data-testid="nearby-section">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
                Perto de você
              </h2>
              <div className="flex items-center gap-3 bg-white border border-[var(--border)] rounded-xl px-4 py-2" data-testid="nearby-radius-control">
                <label htmlFor="nearby-radius-slider" className="text-xs font-medium text-gray-600 whitespace-nowrap">
                  Raio: <span className="text-[var(--primary)] font-bold">{nearbyRadius} km</span>
                </label>
                <input
                  id="nearby-radius-slider"
                  type="range"
                  min={1}
                  max={50}
                  step={1}
                  value={nearbyRadius}
                  onChange={(e) => setNearbyRadius(Number(e.target.value))}
                  className="w-32 sm:w-44 accent-[var(--primary)]"
                  data-testid="nearby-radius-slider"
                  aria-label="Raio de busca em quilômetros"
                />
              </div>
            </div>
            {filteredNearbyBusinesses.length > 0 ? (
              <HorizontalCarousel testId="nearby-carousel">
                {filteredNearbyBusinesses.map(business => (
                  <BusinessCard key={business.id} business={business} showDistance={true} />
                ))}
              </HorizontalCarousel>
            ) : (
              <div className="bg-gray-50 border border-dashed border-gray-300 rounded-xl py-8 text-center text-sm text-gray-500" data-testid="nearby-empty">
                Nenhum negócio encontrado em <strong>{nearbyRadius} km</strong>. Aumente o raio acima para ampliar a busca.
              </div>
            )}
            {/* CTA para ver todos os negócios — atalho para /explorar. */}
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => navigate('/explorar')}
                className="group inline-flex items-center gap-2 rounded-full bg-white border-2 border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-white px-6 py-3 text-sm font-semibold shadow-sm hover:shadow-md transition-all"
                data-testid="nearby-see-all-btn"
              >
                Ver todos os negócios
                <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </section>
        )}

        {/* Feed: acontecendo agora */}
        <FeedWidget municipioSlug={getUserChosenMunicipio()?.slug} />

        {/* O que a cidade está buscando (feature flag gated) */}
        <CidadeBuscandoWidget municipioSlug={getUserChosenMunicipio()?.slug} />

        {/* Coletivos da cidade */}
        <ColetivosWidget municipioSlug={getUserChosenMunicipio()?.slug} />

        {/* Próximas ações das ONGs */}
        <ProximasAcoesWidget municipioSlug={getUserChosenMunicipio()?.slug} />

        {/* New Businesses */}
        {filteredNewBusinesses.length > 0 && (
          <section data-testid="new-section">
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Manrope' }}>
              Novos na plataforma
            </h2>
            <HorizontalCarousel testId="new-carousel">
              {filteredNewBusinesses.map(business => (
                <BusinessCard key={business.id} business={business} />
              ))}
            </HorizontalCarousel>
          </section>
        )}

        {/* Popular Businesses */}
        {filteredPopularBusinesses.length > 0 && (
          <section data-testid="popular-section">
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Manrope' }}>
              Mais procurados
            </h2>
            <HorizontalCarousel testId="popular-carousel">
              {filteredPopularBusinesses.map(business => (
                <BusinessCard key={business.id} business={business} />
              ))}
            </HorizontalCarousel>
          </section>
        )}

        {/* Featured Social Organizations - NEW */}
        {featuredOrgs.length > 0 && (
          <section data-testid="social-orgs-section" className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Heart className="w-6 h-6 text-pink-500" />
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
                  Projetos Sociais em Destaque
                </h2>
              </div>
              <button
                onClick={() => navigate(`${base}/social`)}
                className="text-[var(--primary)] font-medium flex items-center gap-1 hover:underline"
                data-testid="see-all-orgs"
              >
                Ver todos <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {featuredOrgs.map(org => (
                <div
                  key={org.id}
                  onClick={() => navigate(`${base}/social/${org.id}`)}
                  className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-lg hover:border-pink-200 transition-all cursor-pointer group"
                  data-testid={`social-org-card-${org.id}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    {org.avatar_url ? (
                      <img 
                        src={resolveBackendImage(org.avatar_url)} 
                        alt={org.name}
                        className="w-12 h-12 rounded-xl object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-100 to-purple-100 flex items-center justify-center">
                        <Heart className="w-6 h-6 text-pink-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm truncate group-hover:text-[var(--primary)] transition-colors">
                        {org.name}
                      </h3>
                      <p className="text-xs text-gray-500 truncate">
                        {org.neighborhood}
                      </p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                    {org.short_description}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {(org.areas_of_action || []).slice(0, 2).map(area => (
                      <span 
                        key={area}
                        className="bg-pink-50 text-pink-700 text-xs px-2 py-0.5 rounded-full"
                      >
                        {area === 'assistencia_social' ? 'Assistência' :
                         area === 'educacao' ? 'Educação' :
                         area === 'cultura' ? 'Cultura' :
                         area === 'saude' ? 'Saúde' :
                         area === 'meio_ambiente' ? 'Meio Ambiente' :
                         area === 'causa_animal' ? 'Causa Animal' :
                         area}
                      </span>
                    ))}
                    {/* Badge Verificado removido da exibição pública (iter 196). */}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Upcoming Social Activities/Events - NEW */}
        {upcomingActivities.length > 0 && (
          <section data-testid="social-activities-section" className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-6 h-6 text-purple-500" />
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
                  Agenda Social
                </h2>
              </div>
              <button
                onClick={() => navigate(`${base}/social`)}
                className="text-[var(--primary)] font-medium flex items-center gap-1 hover:underline"
                data-testid="see-all-activities"
              >
                Ver todas <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {upcomingActivities.map(activity => (
                <div
                  key={activity.id}
                  onClick={() => navigate(`${base}/social/${activity.org_id}`)}
                  className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-100 p-4 hover:shadow-lg transition-all cursor-pointer group"
                  data-testid={`social-activity-card-${activity.id}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    {activity.org_avatar ? (
                      <img 
                        src={activity.org_avatar} 
                        alt={activity.org_name}
                        className="w-8 h-8 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                        <Heart className="w-4 h-4 text-purple-400" />
                      </div>
                    )}
                    <span className="text-xs text-purple-600 font-medium truncate">
                      {activity.org_name}
                    </span>
                  </div>
                  <h3 className="font-semibold text-sm mb-2 line-clamp-2 group-hover:text-[var(--primary)] transition-colors">
                    {activity.title}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    {activity.start_datetime && (
                      <span className="flex items-center gap-1 bg-white/50 px-2 py-1 rounded-full">
                        <Calendar className="w-3 h-3" />
                        {new Date(activity.start_datetime).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
                      </span>
                    )}
                    {activity.location_text && (
                      <span className="flex items-center gap-1 bg-white/50 px-2 py-1 rounded-full truncate">
                        <MapPin className="w-3 h-3" />
                        {activity.location_text}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Benefits Slider - Before CTA */}
        <BenefitsSlider />

        {/* CTA Banner */}
        <section className="bg-gradient-to-r from-[#56007F] to-[#7B2FA8] rounded-2xl p-8 text-white" data-testid="cta-banner">
          <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ fontFamily: 'Manrope' }}>
            Tem um negócio?
          </h2>
          <p className="text-white/90 mb-6 text-lg">
            Cadastre grátis e apareça para sua comunidade.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate(`${base}/cadastrar`)}
              className="bg-[var(--secondary)] text-[var(--secondary-foreground)] hover:bg-[#FF9500] rounded-full px-8 py-3 font-semibold transition-all shadow-lg hover:shadow-xl active:scale-95"
              data-testid="cta-register-button"
            >
              Cadastrar meu negócio
            </button>
            <button
              className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-3 font-semibold transition-all"
              data-testid="cta-how-it-works"
              onClick={() => navigate(`${base}/sobre`)}
            >
              Como funciona
            </button>
          </div>
        </section>
      </div>

      {/* Partners Slider - Before Footer */}
      <PartnersSlider municipioSlug={municipioSlug} />

      {/* Latest News Section - After Partners */}
      {latestNews.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 py-10">
          <section data-testid="news-section">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
                Últimas Notícias
              </h2>
              <button
                onClick={() => navigate(`${base}/noticias`)}
                className="text-[var(--primary)] font-medium flex items-center gap-1 hover:underline"
                data-testid="see-all-news"
              >
                Ver todas <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {latestNews.map((news) => (
                <div
                  key={news.id || news.slug}
                  onClick={() => navigate(`${base}/noticias/${news.slug || news.id}`)}
                  className="bg-white rounded-xl border border-[var(--border)] overflow-hidden hover:shadow-md transition-all cursor-pointer group"
                  data-testid={`news-card-${news.id}`}
                >
                  {/* News Image */}
                  <div className="h-36 bg-gradient-to-br from-[#56007F]/10 to-[#FFA52A]/10 overflow-hidden">
                    {news.capa_url ? (
                      <img 
                        src={resolveBackendImage(news.capa_url)} 
                        alt={news.titulo} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Newspaper className="w-12 h-12 text-[var(--primary)]/30" />
                      </div>
                    )}
                  </div>
                  {/* News Content */}
                  <div className="p-4">
                    {news.tags && news.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {news.tags.slice(0, 2).map(tag => (
                          <span 
                            key={tag} 
                            className="text-xs bg-[var(--primary)]/10 text-[var(--primary)] px-2 py-0.5 rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <h3 className="font-semibold text-sm mb-2 line-clamp-2 group-hover:text-[var(--primary)] transition-colors">
                      {news.titulo}
                    </h3>
                    {news.resumo && (
                      <p className="text-xs text-[var(--text-secondary)] line-clamp-2 mb-3">
                        {news.resumo}
                      </p>
                    )}
                    <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                      <Calendar className="w-3.5 h-3.5" />
                      {news.publicado_em ? new Date(news.publicado_em).toLocaleDateString('pt-BR', { 
                        day: '2-digit', 
                        month: 'short',
                        year: 'numeric'
                      }) : 'Recente'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {/* Footer */}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onSuccess={() => navigate(`${base}/cadastrar?intent=empreendedor`)}
        initialTab="empreendedor"
      />
    </div>
  );
}
